import { Injectable } from '@angular/core';
import { Observable, of, range } from 'rxjs';
import { mergeMap, reduce, retry, switchMap } from 'rxjs/operators';

import { HttpClient, } from '@angular/common/http';
import { environment } from '@src/environments/environment';

export interface Category {
  id: string;
  name: string;
  companyId: string;
  divisions: string[];
  color: string;
  type: string;
  visible: boolean;
  status: string;
  created?: string;
  modified?: string;
  managedEntity?: {
    managedSourceId?: string;
    managedSource?: string;
    externallyManaged?: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ZonesApiService {
  endpoint = environment.coreEntityApiBase.url;
  perPage = 500;
  retryCount = 3;

  constructor(
    private httpClient: HttpClient
  ) {}

  /**
   * Get the company category data
   * @param companyId the current company
   * @returns Observable containing all category from all pages
   */
  public getCategories(companyId: string): Observable<Category[]> {
    const url = `${this.endpoint}/categories?companyId=${companyId}&status=ACTIVE&q=visible:true`;
    return this.getAllPages<Category>(url, this.perPage);
  }

  /**
   * Helper method to fetch all pages of data from an API
   * @param apiUrl The base API URL
   * @param perPage Number of records per page
   * @returns Observable containing all data from all pages
   */
  private getAllPages<T>(apiUrl: string, perPage: number = 500): Observable<T[]> {
    // Get the total number of records using HEAD request
    return this.getTotalRecords(apiUrl).pipe(
      switchMap((totalRecords) => {
        const totalPages = Math.ceil(totalRecords / perPage);

        // Generate an observable range of page numbers
        return range(1, totalPages);
      }),
      // Fetch data for each page
      mergeMap((page) => this.getDataByPage(apiUrl, page, perPage).pipe(retry(this.retryCount))),
      // Reduce all pages into a single array
      reduce((acc, data) => acc.concat(data), []) 
    );
  }

  /**
   * Get the total number of records using HEAD request
   * @param apiUrl The base API URL
   * @returns Observable containing the total number of records
   */
  private getTotalRecords(apiUrl: string): Observable<number> {
    return this.httpClient.head(apiUrl, { observe: 'response' }).pipe(
      mergeMap((response) => {
        const totalRecords = response.headers?.get('X-Total-Count') || 0;

        return of(Number(totalRecords));
      })
    );
  }

  /**
   * Fetch data for a specific page
   * @param apiUrl The base API URL
   * @param page Page number to fetch
   * @param limit Number of records per page
   * @returns Observable containing data for the specified page
   */
  private getDataByPage<T>(apiUrl: string, page: number, perPage: number): Observable<T[]> {
    const url = `${apiUrl}&page=${page}&per_page=${perPage}`
    return this.httpClient.get<T[]>(url);
  }
}


